/**
 * Iter53 P33 — Mobile BottomSheets extras din SaleDetail.js.
 *
 * Două sheet-uri mobile (vizibile doar pe `md:hidden`):
 *  - MobileTabSheet: selector de tab cu listă verticală (înlocuiește TabsList orizontal pe mobil)
 *  - MobileQuickInfoSheet: replica sidebar-ului „Informații Rapide" (Client / Agenție / Produs / Locație / Metadata)
 *
 * Sunt extrași în același fișier pentru proximitate logică (ambele sunt mobile sheets pentru SaleDetail).
 */
import { XCircle, Check, User, Building2, Package, MapPin } from "lucide-react";
import { CopyableValue } from "../CopyableValue";

// ====== TabSheet ======

export function MobileTabSheet({ open, onClose, tabOptions, activeTab, onTabSelect }) {
  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 md:hidden"
        onClick={onClose}
        data-testid="mobile-tab-sheet-backdrop"
        aria-hidden="true"
      />
      <div
        className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-slate-200 bg-white p-4 shadow-2xl md:hidden"
        data-testid="mobile-tab-sheet"
        role="dialog"
        aria-label="Selectează secțiune"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Selectează secțiune</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-slate-100"
            data-testid="mobile-tab-sheet-close"
            aria-label="Închide"
          >
            <XCircle className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        <div className="flex flex-col gap-1">
          {tabOptions.map((opt) => {
            const isActive = activeTab === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onTabSelect(opt.value); onClose(); }}
                className={`flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#1e3a8a] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
                data-testid={`mobile-tab-option-${opt.value}`}
              >
                <span>{opt.label}</span>
                {isActive && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ====== QuickInfoSheet ======

export function MobileQuickInfoSheet({ open, onClose, sale, clientDisplay }) {
  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 md:hidden"
        onClick={onClose}
        data-testid="mobile-quickinfo-backdrop"
        aria-hidden="true"
      />
      <div
        className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-slate-200 bg-white p-4 shadow-2xl md:hidden"
        data-testid="mobile-quickinfo-sheet"
        role="dialog"
        aria-label="Informații rapide"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Informații Rapide</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-slate-100"
            data-testid="mobile-quickinfo-close"
            aria-label="Închide"
          >
            <XCircle className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <User className="h-4 w-4 text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-500">Client</p>
              <p className="text-sm font-medium text-slate-900 truncate">{clientDisplay}</p>
            </div>
          </div>
          {sale?.agency && (
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">Agenție</p>
                <p className="text-sm font-medium text-slate-900 truncate">{sale.agency.name}</p>
              </div>
            </div>
          )}
          {sale?.contract?.product_name && (
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <Package className="h-4 w-4 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">Produs</p>
                <p className="text-sm font-medium text-slate-900 truncate">{sale.contract.product_name}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <MapPin className="h-4 w-4 text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-500">Locație</p>
              <p className="text-sm font-medium text-slate-900 truncate">
                {sale?.client?.county || "-"}, {sale?.client?.locality || "-"}
              </p>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Creat la</span>
              <span className="text-slate-900">
                {sale?.created_at ? new Date(sale.created_at).toLocaleDateString("ro-RO") : "-"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">ID</span>
              <CopyableValue value={sale?.id} mono truncate label="Sale ID" className="text-xs" />
            </div>
            {sale?.contract_id && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Contract ID</span>
                <CopyableValue value={sale.contract_id} mono truncate label="Contract ID" className="text-xs" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
